import React from "react";
import { Link } from "react-router-dom";

const EMAIL = "team@altezamarketplace.com";

// Render an answer string: split into lines, linkify the support email.
const linkify = (text) =>
  text.split(EMAIL).reduce((acc, part, i, arr) => {
    acc.push(<React.Fragment key={`t${i}`}>{part}</React.Fragment>);
    if (i < arr.length - 1)
      acc.push(
        <a key={`l${i}`} href={`mailto:${EMAIL}`} className="text-primary hover:underline">{EMAIL}</a>
      );
    return acc;
  }, []);

const Answer = ({ text }) => (
  <div className="mt-2 text-sm text-muted-foreground leading-relaxed space-y-1.5">
    {text.split("\n").map((line, i) => (
      <p key={i}>{linkify(line)}</p>
    ))}
  </div>
);

const GROUPS = [
  {
    heading: "Shopping on Alteza",
    sections: [
      {
        title: "The basics",
        items: [
          { q: "What is Alteza Marketplace?", a: "Alteza Marketplace is a curated online marketplace featuring handmade products and select independent brands.\nOur goal is to make it easier to discover unique products while giving small businesses another place to grow and connect with customers." },
          { q: "Who sells products on Alteza?", a: "Products on Alteza are sold by independent sellers and brands.\nEach seller manages their own products, inventory, shipping, and order fulfillment through their Alteza shop." },
          { q: "Do I need an account to shop?", a: "You may be asked to create or sign into an account depending on the features you are using on Alteza.\nHaving an account makes it easier to manage your information and keep track of your activity on the marketplace." },
        ],
      },
      {
        title: "Orders & Shipping",
        items: [
          { q: "Who ships my order?", a: "Orders are shipped directly by the individual seller you purchased from.\nAlteza does not hold or ship seller inventory." },
          { q: "How much does shipping cost?", a: "Each seller sets their own shipping rates.\nShipping costs will be shown during checkout before your purchase is completed." },
          { q: "Why am I being charged more than one shipping fee?", a: "Alteza is a multi-seller marketplace.\nIf you purchase products from more than one seller in the same order, each seller may have their own shipping charge because your items are being shipped separately." },
          { q: "When will my order ship?", a: "Processing and shipping times vary by seller and product.\nPlease review the information provided on the product listing or seller shop before ordering.\nIf you have a question about an existing order, contact the seller first whenever possible. If you still need assistance, email team@altezamarketplace.com." },
          { q: "Will items from different sellers arrive together?", a: "Not necessarily.\nIf your order contains products from multiple Alteza sellers, your items may arrive in separate packages and at different times." },
        ],
      },
      {
        title: "Returns, Refunds & Order Issues",
        items: [
          { q: "Can I return an item?", a: "Return and refund eligibility may vary depending on the seller and the product purchased.\nPlease review the seller's policies and product information before purchasing." },
          { q: "What should I do if there is a problem with my order?", a: "If there is an issue with an order, contact the seller first so they have an opportunity to help resolve it.\nIf you cannot resolve the issue directly with the seller, contact the Alteza team at team@altezamarketplace.com.\nPlease include your order information and a description of the issue." },
          { q: "How are refunds processed?", a: "Approved refunds are processed back through the original payment method.\nRefund timing may vary depending on the payment provider or financial institution." },
        ],
      },
    ],
  },
  {
    heading: "Selling on Alteza",
    sections: [
      {
        title: "Getting started",
        items: [
          { q: "Who can apply to sell on Alteza?", a: "Alteza welcomes handmade businesses and select independent brands that fit the marketplace.\nBecause Alteza is curated, seller applications may be reviewed before a shop is approved." },
          { q: "How much does it cost to sell on Alteza?", a: "There are currently no monthly membership fees or listing fees.\nAlteza charges an 8% marketplace commission on sales.\nStandard payment processing fees may also apply through Stripe." },
          { q: "Do I have to leave Etsy or my own website to sell on Alteza?", a: "No.\nAlteza is another sales channel for your business.\nYou can continue selling through Etsy, your own website, markets, social media, or other platforms while also selling on Alteza." },
          { q: "How do sellers get paid?", a: "Seller payments are managed securely through Stripe Connect.\nSellers must connect their Stripe account before receiving payouts.\nAlteza processes seller payouts weekly, typically on Thursdays. Stripe processing times and your financial institution may affect when funds appear in your account." },
          { q: "Do sellers set their own prices?", a: "Yes.\nSellers control the pricing for their own products." },
          { q: "Do sellers manage their own inventory?", a: "Yes.\nEach seller is responsible for maintaining accurate inventory quantities for their products." },
        ],
      },
      {
        title: "Adding Products",
        items: [
          { q: "How do I create a product listing?", a: "Sign into your seller account and open your seller dashboard.\nFrom there, choose the option to add a new product and complete the required product information. This may include:\n• Product name\n• Description\n• Price\n• Product images\n• Inventory quantity\n• Shipping information\n• Category and other product details\nReview your listing carefully before publishing it." },
          { q: "Can I add product variations?", a: "Yes.\nSellers can create product options or variants for products that come in different sizes, colours, scents, styles, quantities, or other options. Where available, each variant can have its own:\n• Price\n• Inventory quantity\n• Option name\n• Image\nFor example, a candle seller may offer the same candle in several scents while keeping everything within one product listing." },
          { q: "Can different variants have different prices?", a: "Yes.\nYou can set individual pricing for variants when necessary.\nProducts with different variant prices may display a starting price such as \u201cFrom $X.\u201d" },
          { q: "Can I add different images for each variant?", a: "Yes.\nVariant images are optional.\nIf you have different photos for specific colours, styles, scents, or other options, you can assign images to those variants." },
        ],
      },
      {
        title: "Seller Shipping",
        items: [
          { q: "Who sets shipping rates?", a: "Each seller sets and manages their own shipping rates.\nWhen setting your shipping costs, make sure they accurately reflect what you expect to pay to package and ship the order." },
          { q: "Who is responsible for shipping orders?", a: "The seller is responsible for packaging and shipping orders placed through their Alteza shop." },
          { q: "Can customers purchase from multiple sellers at once?", a: "Yes.\nCustomers can purchase products from multiple Alteza sellers.\nBecause each seller fulfills their own orders, shipping charges from each seller may be combined during checkout." },
        ],
      },
      {
        title: "Seller Account & Payments",
        items: [
          { q: "Why do I need Stripe?", a: "Alteza uses Stripe Connect to securely manage seller payments and payouts.\nConnecting Stripe allows your business to receive money from orders placed through Alteza." },
          { q: "What is Stripe Connect Express?", a: "Stripe Connect Express is the secure payment system Alteza uses to manage seller payouts.\nYour financial information is handled through Stripe rather than directly by Alteza." },
          { q: "I connected Stripe. What happens next?", a: "Once your Stripe connection is complete, make sure your seller profile and product listings are also completed.\nYou can then continue preparing your shop for customers." },
        ],
      },
    ],
  },
  {
    heading: "Account & Support",
    sections: [
      {
        title: "Account Help",
        items: [
          { q: "I forgot my password. What should I do?", a: "Use the Forgot Password option on the Alteza login page.\nEnter the email address associated with your account and follow the instructions sent to your email.\nBe sure to check your spam or junk folder if you do not see the email." },
          { q: "I'm having trouble logging in.", a: "First, confirm that you are using the same email address you registered with.\nYou can also try resetting your password.\nIf you still cannot access your account, contact team@altezamarketplace.com." },
          { q: "I'm not seeing a new feature on my seller dashboard.", a: "Your browser may occasionally display an older version of the site. Try:\n• Refreshing the page\n• Closing the browser tab and reopening Alteza\n• Signing out and back in\n• Clearing your browser cache if necessary\nIf the issue continues, email team@altezamarketplace.com and include a screenshot if possible." },
        ],
      },
      {
        title: "Seller Support",
        items: [
          { q: "I need help setting up my shop.", a: "We're happy to help.\nEmail team@altezamarketplace.com with your shop name and a description of what you need assistance with." },
          { q: "I found a technical issue.", a: "Please email team@altezamarketplace.com and include:\n• Your name\n• Your Alteza shop name\n• The email connected to your account\n• A brief description of the problem\n• A screenshot, if available\nProviding this information helps us investigate the issue more quickly." },
        ],
      },
      {
        title: "Partnerships & General Inquiries",
        items: [
          { q: "I'm interested in partnering with Alteza.", a: "We'd love to hear from brands, creators, educators, media, and potential partners who are interested in working with Alteza Marketplace.\nEmail team@altezamarketplace.com and please include a short description of your business or partnership idea." },
        ],
      },
    ],
  },
];

export default function Help() {
  return (
    <div data-testid="help-page" className="max-w-4xl mx-auto px-6 py-24 lg:py-32">
      <div className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-4">◦ Help Centre</div>
      <h1 className="font-serif text-5xl lg:text-6xl tracking-tight font-light">How can we help?</h1>
      <p className="mt-6 text-muted-foreground leading-relaxed max-w-2xl">
        Welcome to the Alteza Help Centre. Whether you're shopping on Alteza or growing your business as an
        Alteza seller, you'll find answers to the most common questions below. Can't find what you're looking
        for? Our team is happy to help at{" "}
        <a href={`mailto:${EMAIL}`} className="text-primary hover:underline">{EMAIL}</a>.
      </p>

      {GROUPS.map((group) => (
        <section key={group.heading} className="mt-16">
          <h2 className="font-serif text-3xl tracking-tight border-b border-border pb-4">{group.heading}</h2>
          {group.sections.map((sec) => (
            <div key={sec.title} className="mt-8">
              <div className="text-xs tracking-[0.2em] uppercase text-primary font-semibold mb-4">◦ {sec.title}</div>
              <div className="space-y-7">
                {sec.items.map((it) => (
                  <div key={it.q} data-testid="help-item">
                    <h3 className="font-medium text-base">{it.q}</h3>
                    <Answer text={it.a} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
      ))}

      <div data-testid="help-still-need-help" className="mt-20 border border-border rounded-sm p-8 bg-secondary/30 text-center">
        <h2 className="font-serif text-2xl mb-2">Still need help?</h2>
        <p className="text-sm text-muted-foreground">
          Email our team at{" "}
          <a href={`mailto:${EMAIL}`} className="text-primary hover:underline">{EMAIL}</a>.
        </p>
        <p className="text-xs text-muted-foreground mt-3">Please include as much information as possible so we can direct your question appropriately.</p>
        <Link to="/contact" className="mt-6 inline-block text-sm text-primary hover:underline">Go to Contact →</Link>
      </div>
    </div>
  );
}
